// Payments tab — "Mark as paid" dialog for a payment taken outside Stripe
// (phone / in-practice / bank transfer / other / email), or for a still-open
// Stripe-link payment the patient ended up paying by another route.
//
// Pure presentation + submit wiring: the parent (PaymentCard) owns "open" and
// passes the payment + api coordinates; this dialog calls markPaymentPaid and
// reports the outcome back via onSuccess/onError so the parent can refresh
// the list and show a snackbar.
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useResponsive } from 'src/hooks';
import { extractApiError } from 'src/utils/extractApiError';
import { markPaymentPaid } from './paymentLinkApi';

// Options match the backend's mode_of_payment choices used for a manual mark
// (SEND/EMAIL-link payment modes are excluded — those are Stripe's own).
export const MARK_PAID_METHOD_OPTIONS = [
    { value: 'PHONE', label: 'Phone' },
    { value: 'INPRACTICE', label: 'In practice' },
    { value: 'BANK_TRANSFER', label: 'Bank transfer' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'OTHER', label: 'Other' },
];

const NOTE_MAX_LENGTH = 500;

export default function MarkAsPaidDialog({
    open,
    onClose,
    payment,
    practiceId,
    leadId,
    treatmentId,
    cancelsOpenLink,
    onSuccess,
    onError,
}) {
    const isMobile = useResponsive('down', 'sm');
    const defaultMethod = MARK_PAID_METHOD_OPTIONS.some((o) => o.value === payment?.mode_of_payment)
        ? payment.mode_of_payment
        : 'PHONE';

    const [method, setMethod] = useState(defaultMethod);
    const [datePaid, setDatePaid] = useState(new Date());
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const today = new Date();
    const isFutureDate = datePaid && dayjs(datePaid).startOf('day').isAfter(dayjs(today).startOf('day'));
    const isValidDate = Boolean(datePaid) && dayjs(datePaid).isValid() && !isFutureDate;
    const noteTooLong = note.length > NOTE_MAX_LENGTH;
    const canSubmit = isValidDate && !noteTooLong && !submitting;

    const handleClose = () => {
        if (submitting) return;
        setMethod(defaultMethod);
        setDatePaid(new Date());
        setNote('');
        onClose();
    };

    const handleConfirm = async () => {
        if (!canSubmit || !payment?.id) return;
        setSubmitting(true);
        try {
            const payload = {
                mode_of_payment: method,
                paid_at: dayjs(datePaid).format('YYYY-MM-DD'),
            };
            const trimmedNote = note.trim();
            if (trimmedNote) payload.note = trimmedNote;

            const res = await markPaymentPaid(practiceId, leadId, treatmentId, payment.id, payload);
            if (res?.ok) {
                onSuccess(res.data);
                handleClose();
            } else {
                onError(extractApiError(res, 'Could not mark this payment as paid.'));
            }
        } catch {
            onError('Could not mark this payment as paid.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Mark as paid</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <Stack spacing={1.5}>
                    {cancelsOpenLink && (
                        <Alert severity="warning" sx={{ fontSize: '0.8rem' }} data-testid="mark-paid-cancels-link-warning">
                            This payment has an open payment link — marking it as paid will cancel that link.
                        </Alert>
                    )}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Payment method"
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        data-testid="mark-paid-method-select"
                    >
                        {MARK_PAID_METHOD_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <DatePicker
                        label="Date paid"
                        value={datePaid}
                        maxDate={today}
                        format="dd/MM/yyyy"
                        onChange={(newValue) => setDatePaid(newValue)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                error: Boolean(datePaid) && !isValidDate,
                                helperText: isFutureDate ? 'Date paid cannot be in the future' : ' ',
                                inputProps: { 'data-testid': 'mark-paid-date-input' },
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="Note (optional)"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        error={noteTooLong}
                        helperText={`${note.length}/${NOTE_MAX_LENGTH}`}
                        inputProps={{ maxLength: NOTE_MAX_LENGTH + 50, 'data-testid': 'mark-paid-note-input' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        £{Number(payment?.amount) || 0} will be recorded as paid.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={submitting}
                    sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                    data-testid="mark-paid-cancel-btn"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!canSubmit}
                    sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                    data-testid="mark-paid-confirm-btn"
                >
                    {submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

MarkAsPaidDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    payment: PropTypes.object,
    practiceId: PropTypes.any,
    leadId: PropTypes.any,
    treatmentId: PropTypes.any,
    cancelsOpenLink: PropTypes.bool,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
};

MarkAsPaidDialog.defaultProps = {
    payment: null,
    practiceId: null,
    leadId: null,
    treatmentId: null,
    cancelsOpenLink: false,
    onSuccess: () => {},
    onError: () => {},
};
